import React from "react";
import { RoomAccessibility } from "@uno/protocol";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../context";
import hostIntegration, { type RoomLaunchIntent } from "../integrations/host";
import useRoomNavigation from "./useRoomNavigation";

const useHostRoomLaunch = () => {
    const { isConnected } = React.useContext(AppContext);
    const navigate = useNavigate();
    const { createRoom, joinRoom } = useRoomNavigation();
    const initialLaunchHandled = React.useRef(false);
    const initialIntent = React.useRef<RoomLaunchIntent | null>(null);
    const [hostReady, setHostReady] = React.useState(false);
    const [requestedRoomId, setRequestedRoomId] = React.useState<string | null>(null);
    const [launchInFlight, setLaunchInFlight] = React.useState(false);

    React.useEffect(() => {
        let active = true;

        void hostIntegration.initialize().then(() => {
            if(active) {
                setHostReady(true);
            }
        });

        return () => {
            active = false;
        };
    }, []);

    React.useEffect(() => {
        return hostIntegration.onRoomJoinRequested(setRequestedRoomId);
    }, []);

    React.useEffect(() => {
        if(
            !hostReady
            || !isConnected
            || launchInFlight
            || (!requestedRoomId && initialLaunchHandled.current)
        ) {
            return;
        }

        let intent: RoomLaunchIntent;
        let launch: (() => Promise<boolean>) | null = null;

        if(requestedRoomId) {
            intent = { type: "join", roomId: requestedRoomId };
        } else {
            intent = initialIntent.current
                ?? hostIntegration.consumeRoomLaunchIntent();
            initialIntent.current = intent;
        }

        if(intent.type === "show-home") {
            navigate("/", { replace: true });
            initialLaunchHandled.current = true;
            return;
        }

        if(intent.type === "join") {
            launch = () => joinRoom(intent.roomId);
        } else if(intent.type === "create-private") {
            launch = () => createRoom(RoomAccessibility.Private);
        }

        if(!launch) {
            initialLaunchHandled.current = true;
            return;
        }

        setLaunchInFlight(true);

        void launch().then((acknowledged) => {
            setLaunchInFlight(false);

            if(!acknowledged) {
                return;
            }

            if(requestedRoomId) {
                initialLaunchHandled.current = true;
                setRequestedRoomId((currentRoomId) =>
                    currentRoomId === requestedRoomId
                        ? null
                        : currentRoomId
                );
            } else {
                initialLaunchHandled.current = true;
            }
        });
    }, [
        createRoom,
        hostReady,
        isConnected,
        joinRoom,
        launchInFlight,
        navigate,
        requestedRoomId,
    ]);
};

export default useHostRoomLaunch;
